import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SectionHeader, StatusBadge, Field, inputCls } from "@/components/Shared";
import DataTable from "@/components/DataTable";
import { InlineEditSelect } from "@/components/InlineEdit";
import HealthBadge from "@/components/HealthBadge";
import HelpHint from "@/components/HelpHint";
import BulkImportButton from "@/components/BulkImportButton";
import { usePermissions } from "@/contexts/PermissionsContext";
import DepartmentPicker, { DepartmentBadge } from "@/components/DepartmentPicker";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import EmployeeFormWizard, { EMPLOYEE_EMPTY } from "@/components/EmployeeFormWizard";
import { Plus, Pencil, Trash2, X, KeyRound } from "lucide-react";
import { toast } from "sonner";

const EMPTY = EMPLOYEE_EMPTY;

export default function Mitarbeiter() {
  const { can } = usePermissions();
  const canCreate = can("mitarbeiter", "create");
  const canEdit = can("mitarbeiter", "edit");
  const canDelete = can("mitarbeiter", "delete");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("aktiv");
  const [deptFilter, setDeptFilter] = useDepartmentFilter("mitarbeiter");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulk, setBulk] = useState({ status: "", department_id: "" });
  // Phase — Onboarding: shows the one-time portal credentials after creating a Fahrer
  const [portalCreds, setPortalCreds] = useState(null);
  const [portalCopied, setPortalCopied] = useState(false);
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  async function load() {
    setLoading(true);
    try {
      const params = { q, status, limit: 5000 };
      if (deptFilter) params.department_id = deptFilter;
      const { data } = await api.get("/employees", { params });
      setItems(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [q, status, deptFilter]);

  // Support ?new=1 query for quick action from topbar
  useEffect(() => {
    if (sp.get("new") === "1") {
      setEditing(null); setForm(EMPTY); setShowForm(true);
      sp.delete("new"); setSp(sp, { replace: true });
    }
    // Phase 32b — Drill-down from Dashboard
    const urlStatus = sp.get("status");
    if (urlStatus !== null && urlStatus !== status) setStatus(urlStatus);
    const urlDept = sp.get("department_id");
    if (urlDept !== null && urlDept !== deptFilter) setDeptFilter(urlDept);
  }, [sp]);

  function toggle(eid) { setSelected((s) => s.includes(eid) ? s.filter((x) => x !== eid) : [...s, eid]); }
  function toggleAll() { setSelected(selected.length === items.length ? [] : items.map((e) => e.id)); }

  async function applyBulk() {
    const payload = { ids: selected };
    if (bulk.status) payload.status = bulk.status;
    if (bulk.department_id) payload.department_id = bulk.department_id;
    if (!bulk.status && !bulk.department_id) { toast.error("Keine Änderungen gewählt"); return; }
    try {
      const { data } = await api.patch("/employees/bulk", payload);
      toast.success(`${data.modified} Mitarbeiter aktualisiert`);
      setSelected([]); setShowBulk(false); setBulk({ status: "", department_id: "" }); load();
    } catch { toast.error("Fehler"); }
  }

  async function save() {
    // Strip transient flags before posting to /api/employees
    const { _enable_portal_on_create, _portal_username, ...employeePayload } = form;
    try {
      if (editing) {
        await api.put(`/employees/${editing.id}`, employeePayload);
        toast.success("Mitarbeiter aktualisiert");
        setShowForm(false); setEditing(null); setForm(EMPTY); load();
        return;
      }
      // Create employee
      const { data: created } = await api.post("/employees", employeePayload);
      // Phase 5Z.36 — surface auto-init summary so the user knows what was created.
      const ai = created?._auto_init;
      if (ai?.vacation || ai?.overtime || ai?.rule_group) {
        const parts = [];
        if (ai.vacation?.entitlement_days != null) {
          parts.push(`Urlaubskonto · ${ai.vacation.entitlement_days} Tage`);
        }
        if (ai.overtime) parts.push("Überstunden-Konto");
        if (ai.rule_group) {
          parts.push(`Lohngruppe „${ai.rule_group.group_label}"${ai.rule_group.created_new_group ? " (neu erstellt)" : ""}`);
        }
        toast.success(`Mitarbeiter angelegt · ${parts.join(" · ")}`, { duration: 6000 });
      } else {
        toast.success("Mitarbeiter angelegt");
      }

      // Optionally activate Fahrer-Portal in the same flow
      const shouldEnablePortal = employeePayload.role === "fahrer" && _enable_portal_on_create;
      if (shouldEnablePortal && created?.id) {
        try {
          const body = {};
          if ((_portal_username || "").trim()) body.username = _portal_username.trim().toLowerCase();
          const { data: portal } = await api.post(`/employees/${created.id}/enable-portal`, body);
          setPortalCreds({
            employee_name: `${created.first_name} ${created.last_name}`,
            employee_id: created.id,
            username: portal.username,
            password: portal.initial_password,
            login_url: portal.login_url || "/fahrer/login",
          });
          toast.success("Fahrer-Portal aktiviert — Zugangsdaten anzeigen!");
        } catch (e) {
          toast.error("Mitarbeiter angelegt, aber Portal konnte nicht aktiviert werden: "
            + (e.response?.data?.detail || "Unbekannter Fehler"));
        }
      }

      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    }
  }

  async function copyPortalCreds() {
    if (!portalCreds) return;
    const text =
      `Fahrer-Portal — Zugangsdaten für ${portalCreds.employee_name}\n` +
      `Login-URL: ${window.location.origin}${portalCreds.login_url}\n` +
      `Benutzername: ${portalCreds.username}\n` +
      `Passwort: ${portalCreds.password}\n\n` +
      `Das Passwort muss beim ersten Login geändert werden.`;
    try {
      await navigator.clipboard.writeText(text);
      setPortalCopied(true);
      setTimeout(() => setPortalCopied(false), 2500);
      toast.success("In die Zwischenablage kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen — bitte manuell markieren");
    }
  }

  async function remove(id) {
    if (!window.confirm("Mitarbeiter wirklich löschen?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Gelöscht"); load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  }

  async function inlineStatus(id, newStatus) {
    if (newStatus === "aktiv") {
      // Workflow guard: warn if blocking docs are expired
      try {
        const { data: defaults } = await api.get(`/employees/${id}/defaults`);
        if (defaults.any_blocking) {
          const list = defaults.blocking_docs.filter(b => b.blocking).map(b => `• ${b.doc_type} (abgelaufen ${b.expiry_date})`).join("\n");
          if (!window.confirm(`Mitarbeiter hat abgelaufene Pflicht-Dokumente:\n${list}\n\nTrotzdem auf "aktiv" setzen?`)) {
            throw new Error("Abgebrochen");
          }
        }
      } catch (e) {
        if (e.message === "Abgebrochen") throw e;
        // /defaults call failed → continue without check
      }
    }
    await api.put(`/employees/${id}`, { status: newStatus });
    setItems((cur) => cur.map((e) => e.id === id ? { ...e, status: newStatus } : e));
    toast.success(`Status: ${newStatus}`);
  }

  function startEdit(emp) {
    setEditing(emp); setForm({ ...EMPTY, ...emp }); setShowForm(true);
  }

  return (
    <div className="p-6 md:p-8" data-testid="mitarbeiter-page">
      <SectionHeader
        title="Mitarbeiter"
        subtitle="Fahrer und Personal verwalten"
        actions={
          <div className="flex gap-2">
            {selected.length > 0 && (
              <button data-testid="bulk-emp-btn" onClick={() => setShowBulk(true)}
                className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm font-medium px-4 py-2 rounded-md hover:bg-amber-500/20">
                Massenaktion ({selected.length})
              </button>
            )}
            {canEdit && (
              <button
                data-testid="download-portal-credentials"
                onClick={async () => {
                  try {
                    const res = await api.get("/employees/portal-credentials/latest", { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "Portal-Zugaenge.xlsx";
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("Portal-Zugangsdatei heruntergeladen — bitte sicher verteilen!");
                  } catch (e) {
                    toast.error(e.response?.status === 404 ? "Noch keine Datei erstellt" : "Fehler beim Download");
                  }
                }}
                className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-amber-600/80 text-zinc-100 text-sm font-medium px-4 py-2 rounded-md transition-colors"
                title="Excel mit allen 274 Fahrer-Portal-Zugängen herunterladen"
              >
                <KeyRound className="w-4 h-4" /> Portal-Zugänge (Excel)
              </button>
            )}
            {canCreate && (
              <BulkImportButton
                entity="employees"
                templateUrl="/imports/employees/template"
                uploadUrl="/imports/employees"
                templateFileName="Mitarbeiter_Vorlage.xlsx"
                label="Bulk-Import"
                testid="bulk-import-employees"
                onDone={load}
              />
            )}
            {canCreate && (
              <button
                data-testid="add-employee-btn"
                onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                <Plus className="w-4 h-4" /> Mitarbeiter hinzufügen
              </button>
            )}
          </div>
        }
      />

      <HelpHint title="Wie funktioniert die Mitarbeiter-Verwaltung?" className="mb-4" testid="help-mitarbeiter">
        <p>Hier verwaltest du <b>Fahrer</b> und <b>Office-Personal</b>. Klicke auf eine Zeile, um das Profil mit allen Stammdaten, Dokumenten, Verträgen und Lohn-Daten zu öffnen.</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><b>Status</b>: <i>Aktiv</i> = arbeitet aktuell, taucht überall in Pickern auf. <i>Inaktiv/Abgemeldet</i> = ausgeschieden, wird in Aufgaben-/Karten-Pickern automatisch ausgeblendet.</li>
          <li><b>Rolle</b> bestimmt die Berechtigungen (Fahrer / Disponent / Backoffice / Buchhaltung / Fuhrparkleiter / GF / Admin) — siehe <i>Sicherheit → Rollen</i>.</li>
          <li><b>Abteilung</b> ist Pflicht für Disponenten — sie steuert was sie sehen dürfen (Amazon, GLS, UPS …).</li>
          <li><b>Portal-Zugänge.xlsx</b> oben rechts: Sammel-Datei mit allen frisch erzeugten Login-Daten — bitte sicher ausdrucken und vernichten.</li>
          <li><b>Massenaktion</b> erscheint, wenn du Zeilen markierst — Status-Wechsel oder Abteilungs-Zuweisung in einem Schritt.</li>
        </ul>
      </HelpHint>

      <DataTable
        rows={items}
        loading={loading}
        searchTerm={q}
        onSearchChange={setQ}
        searchPlaceholder="Name, E-Mail, Telefon..."
        testid="mitarbeiter-table"
        emptyTitle="Keine Mitarbeiter gefunden"
        emptyHint={q || status ? "Versuchen Sie andere Filter." : "Fügen Sie den ersten Mitarbeiter hinzu."}
        filters={
          <div className="flex items-center gap-2 flex-wrap">
            <FirmFilter compact />
            <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="mitarbeiter" testid="mitarbeiter-dept-filter"/>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="mitarbeiter-status-filter"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:ring-2 focus:ring-blue-600 focus:outline-none">
              <option value="">Alle Status</option>
              <option value="aktiv">Aktiv</option>
              <option value="urlaub">Urlaub</option>
              <option value="krank">Krank</option>
              <option value="inaktiv">Inaktiv</option>
            </select>
          </div>
        }
        columns={[
          {
            key: "_select",
            label: (
              <input
                type="checkbox"
                checked={items.length > 0 && selected.length === items.length}
                onChange={toggleAll}
                onClick={(ev) => ev.stopPropagation()}
                data-testid="mitarbeiter-select-all"
                className="rounded cursor-pointer"
              />
            ),
            headerClassName: "w-8",
            render: (e) => (
              <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggle(e.id)} onClick={(ev) => ev.stopPropagation()} data-testid={`emp-select-${e.id}`} className="rounded" />
            ),
          },
          {
            key: "last_name", label: "Name", sortable: true,
            render: (e) => (
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/mitarbeiter/${e.id}`)}>
                <EmpAvatar emp={e} />
                <span className="text-zinc-100 font-medium">{e.first_name} {e.last_name}</span>
              </div>
            ),
          },
          { key: "department", label: "Abteilung", sortable: true, render: (e) => (
            e.department_id
              ? <DepartmentBadge id={e.department_id}/>
              : <span className="text-zinc-300">{e.department || "—"}</span>
          ) },
          { key: "role", label: "Rolle", sortable: true, render: (e) => <span className="text-zinc-300 capitalize">{e.role}</span> },
          { key: "email", label: "E-Mail", render: (e) => <span className="text-zinc-400 font-mono text-xs">{e.email || "—"}</span> },
          { key: "phone", label: "Telefon", render: (e) => <span className="text-zinc-400 font-mono text-xs">{e.phone || "—"}</span> },
          {
            key: "_health", label: "Health",
            render: (e) => (
              <HealthBadge
                level={e.health_level || "ok"}
                reasons={e.health_reasons || []}
                size="xs"
                variant="badge"
                testid={`emp-health-${e.id}`}
              />
            ),
          },
          { key: "status", label: "Status", sortable: true, render: (e) => (
            canEdit ? (
              <InlineEditSelect
                value={e.status}
                options={[["aktiv","Aktiv"],["urlaub","Urlaub"],["krank","Krank"],["inaktiv","Inaktiv"]]}
                onSave={(v) => inlineStatus(e.id, v)}
                render={(v) => <StatusBadge status={v}/>}
                testid={`emp-status-${e.id}`}
              />
            ) : (
              <StatusBadge status={e.status}/>
            )
          ) },
          {
            key: "_actions", label: "",
            headerClassName: "text-right",
            className: "text-right",
            render: (e) => (
              <>
                {canEdit && <button data-testid={`edit-emp-${e.id}`} onClick={(ev) => { ev.stopPropagation(); startEdit(e); }} className="p-1.5 text-zinc-400 hover:text-blue-400"><Pencil className="w-3.5 h-3.5" /></button>}
                {canDelete && <button data-testid={`delete-emp-${e.id}`} onClick={(ev) => { ev.stopPropagation(); remove(e.id); }} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>}
                {!canEdit && !canDelete && <span className="text-zinc-600 text-xs">—</span>}
              </>
            ),
          },
        ]}
      />

      {showForm && (
        <EmployeeFormWizard
          form={form} setForm={setForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={save} editing={!!editing}
        />
      )}

      {showBulk && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-md">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Massenaktion</div>
                <h3 className="font-display font-semibold text-zinc-50 mt-1">{selected.length} Mitarbeiter aktualisieren</h3>
              </div>
              <button onClick={() => setShowBulk(false)} className="text-zinc-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Status setzen auf">
                <select value={bulk.status} onChange={(e) => setBulk({ ...bulk, status: e.target.value })} className={inputCls}>
                  <option value="">— unverändert —</option>
                  {["aktiv", "urlaub", "krank", "inaktiv"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Abteilung setzen auf">
                <DepartmentPicker value={bulk.department_id} onChange={(id) => setBulk({ ...bulk, department_id: id })} testid="bulk-emp-dept-picker"/>
              </Field>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={applyBulk} data-testid="bulk-emp-apply" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Anwenden</button>
            </div>
          </div>
        </div>
      )}

      {/* One-time Fahrer-Portal credentials display (after onboarding a Fahrer with portal activation) */}
      {portalCreds && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]" data-testid="portal-credentials-modal">
          <div className="bg-zinc-900 border-2 border-amber-500/50 rounded-md w-full max-w-lg">
            <div className="p-5 border-b border-amber-500/30 flex items-start gap-3">
              <KeyRound className="w-6 h-6 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold">Fahrer-Portal aktiviert</div>
                <h3 className="font-display font-semibold text-zinc-50 mt-0.5">
                  Zugangsdaten für {portalCreds.employee_name}
                </h3>
                <p className="text-xs text-amber-200/80 mt-1.5">
                  Das Passwort wird <strong>nur einmal</strong> angezeigt. Bitte jetzt kopieren und dem Fahrer übergeben.
                </p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-zinc-950 border border-zinc-800 rounded p-4 font-mono text-sm space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-zinc-500 text-xs w-28 flex-shrink-0">Login-URL:</span>
                  <span className="text-blue-300 text-xs break-all">{window.location.origin}{portalCreds.login_url}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-zinc-500 text-xs w-28 flex-shrink-0">Benutzername:</span>
                  <span className="text-zinc-100" data-testid="portal-creds-username">{portalCreds.username}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-zinc-500 text-xs w-28 flex-shrink-0">Passwort:</span>
                  <span className="text-amber-200 font-bold tracking-wider" data-testid="portal-creds-password">{portalCreds.password}</span>
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 italic">
                Der Fahrer muss das Passwort beim ersten Login ändern. Verlust = Passwort kann jederzeit im Mitarbeiter-Profil zurückgesetzt werden.
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button
                onClick={copyPortalCreds}
                data-testid="portal-creds-copy"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded font-medium"
              >
                {portalCopied ? "✓ Kopiert!" : "Alle Zugangsdaten kopieren"}
              </button>
              <button
                onClick={() => setPortalCreds(null)}
                data-testid="portal-creds-close"
                className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded"
              >
                Habe kopiert — schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmpAvatar({ emp }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const has = !!emp.profile_image_path;
  if (has) {
    return (
      <img
        src={`${API_URL}/api/employees/${emp.id}/photo`}
        alt={`${emp.first_name} ${emp.last_name}`}
        className="w-8 h-8 rounded-full object-cover border border-zinc-700"
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-blue-600/20 border border-blue-600/30 flex items-center justify-center text-blue-400 text-xs font-bold">
      {(emp.first_name?.[0] || "?") + (emp.last_name?.[0] || "")}
    </div>
  );
}

